import { apiClient } from '@api';
import { BaseUploadMetadata } from '@libs/types';

export type CreateUrlResponse = {
  s3UploadUrl: string;
  s3Key: string;
  documentId: string;
};

// Get presigned upload URL
export const createUploadUrl = async (
  endpoint: string,
  data: { fileName: string; contentType: string; fileSize: number } & Record<string, unknown>,
  signal?: AbortSignal,
): Promise<CreateUrlResponse> => {
  const response = await apiClient.post<CreateUrlResponse>(endpoint, data, { signal });
  return response.data;
};

// Upload to S3 with progress (XHR)
export const uploadToS3 = (
  url: string,
  file: File,
  onProgress: (progress: number) => void,
  signal?: AbortSignal,
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    if (signal) {
      signal.addEventListener('abort', () => {
        xhr.abort();
        reject(new DOMException('Upload cancelled', 'AbortError'));
      });
    }

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        const progress = Math.round((event.loaded / event.total) * 100);
        onProgress(progress);
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`S3 upload failed with status ${xhr.status}`));
      }
    };

    xhr.onerror = () => reject(new Error('Network error during S3 upload'));
    xhr.ontimeout = () => reject(new Error('S3 upload timed out'));

    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.timeout = 5 * 60 * 1000; // 5 minutes
    xhr.send(file);
  });
};

// Confirm upload (generic return type to support full document objects)
export const confirmUpload = async <
  TMetadata extends BaseUploadMetadata,
  TResponse = { documentId: string }
>(
  endpoint: string,
  data: TMetadata,
  signal?: AbortSignal,
): Promise<TResponse> => {
  const response = await apiClient.post<TResponse>(endpoint, data, {
    signal,
  });
  return response.data;
};

// Delete document
export const deleteDocument = async (endpoint: string, signal?: AbortSignal): Promise<void> => {
  const response = await apiClient.delete<void>(endpoint, { signal });
  return response.data;
};
